/**
 * Which app's own built-in browser this page is running in, if it is one we know (RUN 87).
 *
 * Personal sign-in links travel by WhatsApp, and a tap on one can open the page inside the
 * messaging app's own browser instead of Safari or Chrome. A browser like that keeps its storage
 * to itself: a device paired there is paired for THAT browser only, so the phone's real browser
 * and the home-screen app stay signed out, and the single-use link has been spent on a browser
 * the manager will never open again. The sign-in screen therefore shows a gentle hint above
 * `Continue To Sign In` when it recognises one.
 *
 * ADVISORY ONLY, and deliberately so:
 *
 *  - A user-agent string is a claim, not a fact, and it can be wrong in both directions. Nothing
 *    is refused on the strength of it: `Continue To Sign In` stands whatever this returns.
 *  - Only the app's NAME is read, so the hint can say which app it means. Like `deviceLabel`,
 *    this is not a fingerprint: no version, no model, and nothing is kept or sent anywhere.
 *  - An agent it does not recognise returns `null` and no hint is drawn. A missing hint leaves a
 *    manager exactly where they were before this existed; a wrong one would send them away from
 *    a browser that works.
 *
 * The markers are the ones each app adds to the system browser's own agent string: `WhatsApp`,
 * `Instagram`, `FBAN` / `FBAV` / `FB_IAB` (Facebook, Messenger included), `Line/` (LINE) and
 * `MicroMessenger` (WeChat). Safari, Chrome and the other full browsers carry none of them.
 * Instagram is checked before the Facebook markers so that the more specific name wins.
 */

const IN_APP_BROWSERS: readonly { pattern: RegExp; name: string }[] = [
  { pattern: /\bWhatsApp\b/i, name: "WhatsApp" },
  { pattern: /\bInstagram\b/i, name: "Instagram" },
  { pattern: /\bFBAN\b|\bFBAV\b|\bFB_IAB\b/, name: "Facebook" },
  { pattern: /\bLine\//i, name: "LINE" },
  { pattern: /\bMicroMessenger\b/i, name: "WeChat" },
];

/** The app whose built-in browser this is, or `null` for a full browser or an unknown agent. */
export function inAppBrowserName(userAgent: string = window.navigator.userAgent): string | null {
  return IN_APP_BROWSERS.find((browser) => browser.pattern.test(userAgent))?.name ?? null;
}
